import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { verify2fa, resend2fa } from "../services/authService";
import { getLocation } from "utils/Common/location";
import { APIClient, initializeApiClient } from "../utils/api/apiClient";

type AppRole = "admin" | "gc" | "sub" | "lender" | "owner" | "inspector";

const ROLE_REDIRECT: Record<AppRole, string> = {
  admin: "/admin",
  gc: "/gc-dashboard",
  sub: "/lien-waiver",
  lender: "/bank-dashboard",
  owner: "/owner-dashboard",
  inspector: "/project-inspection",
};

interface ApiError {
  message?: string;
}

interface TwoFaTokens {
  accessToken: string;
  refreshToken: string;
}

interface TwoFaUser {
  roles?: string[];
  [key: string]: unknown;
}

interface VerifyResponseData {
  data: {
    user: TwoFaUser;
    tokens: TwoFaTokens;
  };
}

const useTwoFactor = () => {
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const [error, setError] = useState("");
  const [resendSuccess, setResendSuccess] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const twoFactorToken =
    (location.state as { twoFactorToken?: string } | null)?.twoFactorToken ??
    "";

  const selectedRole =
    (location.state as { selectedRole?: string } | null)?.selectedRole ?? "";

  const handleVerify = async (otp: string): Promise<void> => {
    setLoading(true);
    setError("");

    const loc = await getLocation();

    const { data, error: apiError } = (await verify2fa(
      otp,
      twoFactorToken,
      loc,
    )) as {
      data: VerifyResponseData | null;
      error: ApiError | null;
    };

    if (apiError) {
      setError(apiError.message || "Verification failed. Please try again.");
      setLoading(false);
      return;
    }

    const { user, tokens } = data!.data;
    const roles = user.roles ?? [];
    const role = (
      selectedRole && roles.includes(selectedRole) ? selectedRole : (roles[0] ?? "")
    ) as AppRole;

    localStorage.setItem("token", tokens.accessToken);
    localStorage.setItem("refreshToken", tokens.refreshToken);
    localStorage.setItem("role", role);
    localStorage.setItem("user", JSON.stringify({ ...user, role }));
    localStorage.setItem("authChanged", Date.now().toString());

    APIClient.setApiToken(tokens.accessToken);
    APIClient.setRole(role);
    initializeApiClient(role);

    window.dispatchEvent(new CustomEvent("auth-ready"));

    const redirectTo = sessionStorage.getItem("redirect_after_login");
    sessionStorage.removeItem("redirect_after_login");
    navigate(redirectTo || ROLE_REDIRECT[role] || "/", { replace: true });
  };

  const handleResend = async (): Promise<void> => {
    setResending(true);
    setError("");
    setResendSuccess(false);

    const { error: apiError } = await resend2fa(twoFactorToken);

    if (apiError) {
      setError((apiError as ApiError).message || "Failed to resend OTP.");
    } else {
      setResendSuccess(true);
    }
    setResending(false);
  };

  return {
    handleVerify,
    handleResend,
    loading,
    resending,
    error,
    resendSuccess,
    twoFactorToken,
  };
};

export default useTwoFactor;
